import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { getAuth, onAuthStateChanged, getIdTokenResult } from 'firebase/auth';

/**
 * Route guard for admin-only surfaces (user management).
 *
 * Mirrors the `isAdmin` custom-claim check used by DashboardWrapper. The
 * underlying data mutations are already enforced server-side by firestore.rules
 * (users delete / role writes require isAdmin()), so this is UI hardening: a
 * non-admin should not reach the admin surface or be shown destructive controls
 * that only fail at the rules layer. Redirects non-admins to the dashboard.
 */
const RequireAdmin: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<'checking' | 'allowed' | 'denied'>('checking');

  useEffect(() => {
    const auth = getAuth();
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user) { setState('denied'); return; }
      try {
        const token = await getIdTokenResult(user);
        setState(token.claims.isAdmin === true ? 'allowed' : 'denied');
      } catch {
        setState('denied');
      }
    });
    return () => unsub();
  }, []);

  // Brief null while the claim resolves — avoids flashing the admin UI to a
  // non-admin before the redirect fires.
  if (state === 'checking') return null;
  if (state === 'denied') return <Navigate to='/dashboard' replace />;
  return <>{children}</>;
};

export default RequireAdmin;
